import { useEffect, useRef, useState } from "react";
import type { PDFDocumentProxy } from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

// D-83: the worker's URL carries a query so a browser that cached the copy
// production served as application/octet-stream (immutable, for a year)
// fetches it again — Vite's hash is content-based, so the file name alone
// would not change. The type itself is fixed in web/nginx.conf.
const WORKER_SRC = `${workerUrl}${workerUrl.includes("?") ? "&" : "?"}d83`;

/** The caught reason, or what step failed when the rejection carries no text. */
function reasonOf(err: unknown, step: string): string {
  return err instanceof Error && err.message ? err.message : step;
}

/** Canvas rendering also works in browsers without a built-in PDF viewer. */
export function InvoicePdfViewer({ url }: { url: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [document, setDocument] = useState<PDFDocumentProxy | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [rendering, setRendering] = useState(true);

  useEffect(() => {
    let disposed = false;
    let task: ReturnType<typeof import("pdfjs-dist").getDocument> | undefined;
    setDocument(null);
    setPageNumber(1);
    setError(null);
    setRendering(true);
    void import("pdfjs-dist").then(async (pdfjs) => {
      if (disposed) return;
      pdfjs.GlobalWorkerOptions.workerSrc = WORKER_SRC;
      task = pdfjs.getDocument({ url });
      const pdf = await task.promise;
      if (!disposed) setDocument(pdf);
    }).catch((err: unknown) => {
      if (!disposed) { console.error("Invoice PDF preview failed", err); setError(reasonOf(err, "the viewer did not load")); setRendering(false); }
    });
    return () => { disposed = true; void task?.destroy(); };
  }, [url]);

  useEffect(() => {
    if (!document) return;
    let disposed = false;
    let renderTask: ReturnType<Awaited<ReturnType<PDFDocumentProxy["getPage"]>>["render"]> | undefined;
    setRendering(true);
    setError(null);
    void document.getPage(pageNumber).then(async (page) => {
      const canvas = canvasRef.current;
      if (disposed || !canvas) return;
      const viewport = page.getViewport({ scale: 1.5 });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      renderTask = page.render({ canvas, viewport });
      await renderTask.promise;
      if (!disposed) setRendering(false);
    }).catch((err: unknown) => {
      if (!disposed) { console.error("Invoice PDF preview failed", err); setError(reasonOf(err, `page ${pageNumber} did not render`)); setRendering(false); }
    });
    return () => { disposed = true; renderTask?.cancel(); };
  }, [document, pageNumber]);

  return <div>
    {document && <div className="inv-pdf-nav">
      <button type="button" className="button plain" disabled={pageNumber === 1 || rendering} onClick={() => setPageNumber((p) => p - 1)}>Previous</button>
      <span>Page {pageNumber} of {document.numPages}</span>
      <button type="button" className="button plain" disabled={pageNumber === document.numPages || rendering} onClick={() => setPageNumber((p) => p + 1)}>Next</button>
    </div>}
    {rendering && <p role="status">Loading PDF…</p>}
    {/* D-83: the reason is on the screen, not only in the console — the first
        failure on production took a request against the server to explain. */}
    {error !== null && <p role="alert">Could not display PDF. Use Download PDF below. <small className="muted">({error})</small></p>}
    <div className="inv-pdf-scroll">
      <canvas ref={canvasRef} aria-label={`Invoice PDF, page ${pageNumber}`} className="inv-pdf-canvas" hidden={rendering || error !== null} />
    </div>
  </div>;
}
